import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, downloadFile, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, scoreColor } from "@/components/StatusBadge";
import { toast } from "sonner";
import { ProgressChart } from "@/components/ProgressChart";
import { TrendingUp, CalendarCheck, FileQuestion, Loader2, ArrowRight, Download, Lock, Unlock } from "lucide-react";

const WEIGHTS = [
  { key: "kehadiran", label: "Kehadiran", val: 5 },
  { key: "partisipasi", label: "Partisipasi Aktif", val: 15 },
  { key: "kuis", label: "Kuis", val: 20 },
  { key: "individu", label: "Presentasi Individu", val: 30 },
  { key: "kelompok", label: "Presentasi Kelompok", val: 30 },
];

export default function EmployeePortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [attStatus, setAttStatus] = useState(null);
  const [points, setPoints] = useState(null);

  useEffect(() => {
    if (!user?.employee_id) return;
    api.get(`/assessments/${user.employee_id}`).then((r) => setAssessment(r.data));
    api.get(`/attendance?employee_id=${user.employee_id}`).then((r) => setAttendance(r.data));
    api.get("/quiz-attempts").then((r) => setAttempts(r.data));
    api.get(`/assessments/${user.employee_id}/progress`).then((r) => setPoints(r.data.points)).catch(() => setPoints([]));
    api.get("/attendance/status").then((r) => setAttStatus(r.data)).catch(() => {});
  }, [user]);

  if (!assessment) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  const getCert = async () => {
    try {
      await downloadFile(`/assessments/${user.employee_id}/certificate`, `sertifikat_${user.employee_id}.pdf`);
      toast.success("Sertifikat berhasil diunduh");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-slate-900 p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 85% 20%, #25499B 0%, transparent 45%), radial-gradient(circle at 10% 90%, #F16522 0%, transparent 45%)" }} />
        <div className="relative flex flex-wrap items-center gap-5">
          {assessment.photo_url ? <img src={fileUrl(assessment.photo_url)} alt="" className="h-16 w-16 rounded-2xl object-cover ring-2 ring-white/20" /> : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-2xl font-bold">{user.name[0]}</div>}
          <div className="flex-1">
            <p className="text-slate-300 text-sm">Selamat datang kembali,</p>
            <h1 className="font-heading text-2xl font-extrabold">{user.name}</h1>
            <p className="text-slate-400 text-sm">{assessment.position} · {user.employee_id}</p>
          </div>
          <div className="text-right">
            <p className="text-slate-300 text-sm">Nilai Akhir</p>
            <p className={`font-heading text-4xl font-extrabold ${assessment.final_score >= 7 ? "text-emerald-400" : "text-rose-400"}`}>{assessment.final_score}</p>
          </div>
        </div>
        <div className="relative mt-4 flex flex-wrap items-center gap-3">
          <StatusBadge status={assessment.status} />
          {assessment.status === "Direkomendasikan" && (
            <button data-testid="download-certificate-button" onClick={getCert} className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 transition">
              <Download className="h-4 w-4" /> Unduh Sertifikat Kelulusan
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <button data-testid="portal-attendance-cta" onClick={() => navigate("/kehadiran")} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-left hover:border-blue-300 transition">
          <div className="flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><CalendarCheck className="h-6 w-6" /></div>
            <span data-testid="portal-attendance-status" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${attStatus?.is_open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {attStatus === null ? "…" : attStatus.is_open ? <><Unlock className="h-3 w-3" /> PRESENSI DIBUKA</> : <><Lock className="h-3 w-3" /> PRESENSI DITUTUP</>}
            </span>
          </div>
          <p className="mt-4 font-semibold text-slate-900">Absen Kehadiran</p>
          <p className="text-sm text-slate-500">
            {attStatus?.is_open
              ? "Sesi presensi dibuka — silakan tandai kehadiran Anda hari ini."
              : "Sesi presensi belum dibuka oleh admin. Mohon tunggu."}
          </p>
          <p className="mt-1 text-xs text-slate-400">{attendance.length} kehadiran tercatat</p>
        </button>
        <button data-testid="portal-quiz-cta" onClick={() => navigate("/kuis")} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-left hover:border-blue-300 transition">
          <div className="flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><FileQuestion className="h-6 w-6" /></div>
            <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition" />
          </div>
          <p className="mt-4 font-semibold text-slate-900">Ikuti Kuis</p>
          <p className="text-sm text-slate-500">{attempts.length} kuis dikerjakan</p>
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="font-heading font-bold text-slate-900 mb-1">Rincian Komponen Penilaian</h3>
        <p className="text-xs text-slate-400 mb-4">Komponen dinilai sesuai kelompok Anda{assessment.group ? ` (${assessment.group})` : ""}. Bobot dinormalisasi ke 100%.</p>
        <div className="space-y-4">
          {(() => {
            const active = assessment.active_components || WEIGHTS.map((w) => w.key);
            const activeWeights = WEIGHTS.filter((w) => active.includes(w.key));
            const totalVal = activeWeights.reduce((s, w) => s + w.val, 0) || 1;
            return activeWeights.map((w) => {
              const pct = Math.round((w.val / totalVal) * 100);
              return (
                <div key={w.key} data-testid={`portal-component-${w.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700">{w.label} <span className="text-slate-400">({pct}%)</span></span>
                    <span className={`font-bold ${scoreColor(assessment[w.key])}`}>{assessment[w.key]} / 10</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-blue-500" style={{ width: `${(assessment[w.key] / 10) * 100}%` }} />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      <div data-testid="portal-progress-card" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h3 className="font-heading font-bold text-slate-900">Grafik Progres Nilai</h3>
        </div>
        <p className="text-xs text-slate-400 mb-4">Perkembangan nilai akhir Anda setiap kali ada tugas atau kuis yang dinilai.</p>
        {points === null ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
        ) : <ProgressChart points={points} height={260} />}
      </div>
    </div>
  );
}
